/* ============================================================
   LANDING FAMILY — Shared JavaScript
   Handles: scroll reveal, variant selection, checkout redirect,
            smooth scroll, FAQ accordion, compare table
   ============================================================ */

(function () {
  'use strict';

  /* ── Utilities ─────────────────────────────────────────── */

  /**
   * Format Shopify price (cents integer) to currency string.
   * Uses the locale configured in the document or falls back to a safe default.
   */
  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    const amount = (cents / 100).toFixed(2);
    // Try to get currency from window.Shopify if available
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CLP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return '$' + amount;
    }
  }

  /**
   * Find a variant by selected option values.
   * @param {Array} variants - Array of variant objects from product JSON
   * @param {Object} selected - { option1: 'val', option2: 'val', ... }
   */
  function findVariant(variants, selected) {
    return variants.find(function (v) {
      return (
        (!selected.option1 || v.option1 === selected.option1) &&
        (!selected.option2 || v.option2 === selected.option2) &&
        (!selected.option3 || v.option3 === selected.option3)
      );
    }) || null;
  }

  /* ── Scroll Reveal ─────────────────────────────────────── */

  function initScrollReveal() {
    var elements = document.querySelectorAll('.lf-reveal');
    if (!elements.length) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

      elements.forEach(function (el) { observer.observe(el); });
    } else {
      // Fallback: show all immediately
      elements.forEach(function (el) { el.classList.add('is-visible'); });
    }
  }

  /* ── Hero Smooth Scroll ────────────────────────────────── */

  function initHeroScroll() {
    document.querySelectorAll('[data-lf-anchor]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var targetId = btn.getAttribute('data-lf-anchor');
        var target = document.getElementById(targetId) ||
                     document.querySelector('[data-lf-section="' + targetId + '"]');
        if (target) {
          var headerHeight = document.querySelector('#header-group')
            ? document.querySelector('#header-group').offsetHeight
            : 0;
          var top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      });
    });
  }

  /* ── Product Showcase — Variant Picker ─────────────────── */

  function initShowcaseCards() {
    document.querySelectorAll('[data-lf-product-card]').forEach(function (card) {
      var dataEl = card.querySelector('[data-lf-product-json]');
      if (!dataEl) return;

      var productData;
      try {
        productData = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[LandingFamily] Could not parse product JSON', e);
        return;
      }

      var variants = productData.variants || [];
      var options = productData.options || [];

      // Initial state: first variant selected
      var selectedOptions = {};
      if (variants.length > 0) {
        var firstVariant = variants[0];
        options.forEach(function (optName, i) {
          selectedOptions['option' + (i + 1)] = firstVariant['option' + (i + 1)];
        });
      }

      var priceEl     = card.querySelector('[data-lf-price]');
      var compareEl   = card.querySelector('[data-lf-compare-price]');
      var imageEl     = card.querySelector('[data-lf-product-image]');
      var buyBtn      = card.querySelector('[data-lf-buy-btn]');
      var soldOutText = card.querySelector('[data-lf-sold-out]');
      var mpBanner    = card.querySelector('[data-lf-mp-banner]');
      var mpPriceEl   = card.querySelector('[data-lf-mp-price]');

      // Initialize label display
      card.querySelectorAll('[data-lf-option-group]').forEach(function (group) {
        var optIndex = group.getAttribute('data-lf-option-group');
        var selectedValueLabel = group.querySelector('[data-lf-selected-value]');
        var optKey = 'option' + optIndex;
        if (selectedValueLabel && selectedOptions[optKey]) {
          selectedValueLabel.textContent = selectedOptions[optKey];
        }
      });

      // Mark first buttons as selected
      card.querySelectorAll('[data-lf-option-btn]').forEach(function (btn) {
        var optIndex = btn.getAttribute('data-lf-option-index');
        var optValue = btn.getAttribute('data-lf-option-value');
        var optKey = 'option' + optIndex;
        if (selectedOptions[optKey] === optValue) {
          btn.classList.add('is-selected');
        }
      });

      // Update UI to reflect a variant
      function applyVariant(variant) {
        if (!variant) return;

        // Price
        if (priceEl) {
          priceEl.textContent = formatMoney(variant.price);
        }
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.style.display = 'none';
          }
        }
        
        // Mercado Pago Installments
        if (mpBanner && mpPriceEl) {
          var installmentsCount = parseInt(mpBanner.getAttribute('data-installments'), 10) || 3;
          var installmentPrice = variant.price / installmentsCount;
          mpPriceEl.textContent = formatMoney(installmentPrice);
        }

        // Image
        if (imageEl && variant.featured_image) {
          imageEl.src = variant.featured_image;
          imageEl.srcset = '';
        }

        // Availability
        if (buyBtn) {
          buyBtn.setAttribute('data-variant-id', variant.id);
          if (variant.available) {
            buyBtn.removeAttribute('disabled');
            buyBtn.classList.remove('lf-btn--disabled');
            if (soldOutText) soldOutText.style.display = 'none';
          } else {
            buyBtn.setAttribute('disabled', 'disabled');
            buyBtn.classList.add('lf-btn--disabled');
            if (soldOutText) soldOutText.style.display = '';
          }
        }
      }

      // Handle option button clicks
      card.querySelectorAll('[data-lf-option-btn]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var optIndex = btn.getAttribute('data-lf-option-index');
          var optValue = btn.getAttribute('data-lf-option-value');
          var optKey   = 'option' + optIndex;

          // Update selected state for this option group
          var group = card.querySelector('[data-lf-option-group="' + optIndex + '"]');
          if (group) {
            group.querySelectorAll('[data-lf-option-btn]').forEach(function (b) {
              b.classList.remove('is-selected');
            });
            var selectedLabel = group.querySelector('[data-lf-selected-value]');
            if (selectedLabel) selectedLabel.textContent = optValue;
          }
          btn.classList.add('is-selected');
          selectedOptions[optKey] = optValue;

          // Find matching variant
          var matched = findVariant(variants, selectedOptions);
          if (matched) {
            applyVariant(matched);
          }
        });
      });

      // Apply initial variant state
      var initialVariant = findVariant(variants, selectedOptions);
      if (initialVariant) applyVariant(initialVariant);

      // Buy Now → Direct Checkout
      if (buyBtn) {
        buyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (buyBtn.hasAttribute('disabled')) return;

          var variantId = buyBtn.getAttribute('data-variant-id');
          if (!variantId) return;

          // Show loading state
          buyBtn.classList.add('lf-btn--loading');
          var originalText = buyBtn.innerHTML;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
          })
          .then(function (res) {
            if (!res.ok) throw new Error('Cart add failed');
            return res.json();
          })
          .then(function () {
            window.location.href = '/checkout';
          })
          .catch(function (err) {
            console.error('[LandingFamily] Buy Now error:', err);
            buyBtn.classList.remove('lf-btn--loading');
            buyBtn.innerHTML = originalText;
          });
        });
      }
    });
  }

  /* ── Compare Table — Variant Picker & Buy Buttons ────── */

  function initCompareTable() {

    // ── Buy Now buttons ──────────────────────────────────────
    document.querySelectorAll('[data-lf-compare-buy]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        btn.classList.add('lf-btn--loading');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart add failed');
          return res.json();
        })
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          console.error('[LandingFamily] Compare Buy Now error:', err);
          btn.classList.remove('lf-btn--loading');
        });
      });
    });

    // ── Variant pickers in compare columns ───────────────────
    // Each column uses data-compare-option buttons.
    // Variant JSON is embedded in <script id="lf-compare-json-{productId}">.
    document.querySelectorAll('[data-compare-option]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var colId    = btn.getAttribute('data-col-id');
        var optKey   = btn.getAttribute('data-option-index');   // e.g. "option1"
        var optValue = btn.getAttribute('data-option-value');

        // Update selected state within this option group (same colId + same optKey)
        document.querySelectorAll('[data-compare-option][data-col-id="' + colId + '"]').forEach(function (b) {
          if (b.getAttribute('data-option-index') === optKey) {
            b.classList.remove('is-selected');
          }
        });
        btn.classList.add('is-selected');

        // Build current selections for this column
        var selected = {};
        document.querySelectorAll('[data-compare-option][data-col-id="' + colId + '"].is-selected').forEach(function (b) {
          selected[b.getAttribute('data-option-index')] = b.getAttribute('data-option-value');
        });

        // Load variant data from embedded JSON
        var jsonEl = document.getElementById('lf-compare-json-' + colId);
        if (!jsonEl) return;
        var variants;
        try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }

        var matched = variants.find(function (v) {
          return Object.keys(selected).every(function (key) {
            return v[key] === selected[key];
          });
        });

        if (!matched) return;

        // Update price (all elements with data-lf-compare-price="{colId}")
        document.querySelectorAll('[data-lf-compare-price="' + colId + '"]').forEach(function (el) {
          el.textContent = formatMoney(matched.price);
        });

        // Update image
        if (matched.featured_image) {
          document.querySelectorAll('[data-lf-compare-img="' + colId + '"]').forEach(function (img) {
            img.src = matched.featured_image;
          });
        }

        // Update buy buttons (all data-lf-compare-buy="{colId}")
        document.querySelectorAll('[data-lf-compare-buy="' + colId + '"]').forEach(function (b) {
          b.setAttribute('data-variant-id', matched.id);
          if (matched.available) {
            b.removeAttribute('disabled');
            b.classList.remove('lf-btn--disabled');
          } else {
            b.setAttribute('disabled', 'disabled');
            b.classList.add('lf-btn--disabled');
          }
        });
      });
    });
  }


  /* ── FAQ Accordion ─────────────────────────────────────── */

  function initFAQ() {
    document.querySelectorAll('[data-lf-faq-item]').forEach(function (item) {
      var trigger = item.querySelector('[data-lf-faq-trigger]');
      var content = item.querySelector('[data-lf-faq-content]');
      if (!trigger || !content) return;

      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');

        // Close all siblings
        var parent = item.closest('[data-lf-faq-list]');
        if (parent) {
          parent.querySelectorAll('[data-lf-faq-item]').forEach(function (sibling) {
            if (sibling !== item) {
              sibling.classList.remove('is-open');
              var sibContent = sibling.querySelector('[data-lf-faq-content]');
              if (sibContent) {
                sibContent.style.maxHeight = '0';
                sibContent.setAttribute('aria-hidden', 'true');
              }
              var sibTrigger = sibling.querySelector('[data-lf-faq-trigger]');
              if (sibTrigger) sibTrigger.setAttribute('aria-expanded', 'false');
            }
          });
        }

        // Toggle this item
        if (isOpen) {
          item.classList.remove('is-open');
          content.style.maxHeight = '0';
          content.setAttribute('aria-hidden', 'true');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
          content.setAttribute('aria-hidden', 'false');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── SEO Scrubbing Effect ──────────────────────────────── */
  function initSEOScrubbing() {
    const container = document.querySelector('[data-lf-seo-container]');
    const textEl = document.querySelector('[data-lf-seo-text]');
    if (!container || !textEl) return;

    // Only split once
    if (textEl.classList.contains('is-split')) return;
    
    // Split text into words, preserving <br> tags for multiline support
    const htmlContent = textEl.innerHTML;
    // This regex splits by spaces, but also captures <br> tags so they stay in the array
    const nodes = htmlContent.split(/(<br\s*\/?>|\s+)/i);
    
    textEl.innerHTML = nodes.map(node => {
      // Keep <br> intact
      if (node.toLowerCase().indexOf('<br') > -1) return '<br>';
      // Keep whitespace intact
      if (node.trim() === '') return node;
      // Wrap actual words
      return `<span class="lf-seo-scrub-word">${node}</span>`;
    }).join('');
    
    textEl.classList.add('is-split');

    const wordSpans = textEl.querySelectorAll('.lf-seo-scrub-word');
    const totalWords = wordSpans.length;
    if (totalWords === 0) return;

    function onScroll() {
      // Line on the screen where the word lights up (e.g. 65% down from top)
      const triggerLine = window.innerHeight * 0.65;
      
      // Read phase: calculate states first to avoid layout thrashing
      const states = [];
      for (let i = 0; i < wordSpans.length; i++) {
        states.push(wordSpans[i].getBoundingClientRect().top < triggerLine);
      }
      
      // Write phase: update classes
      for (let i = 0; i < wordSpans.length; i++) {
        if (states[i]) {
          wordSpans[i].classList.add('is-filled');
        } else {
          wordSpans[i].classList.remove('is-filled');
        }
      }
    }

    // Use requestAnimationFrame for smooth 60FPS updates
    let ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
    
    // Initial calculation
    onScroll();
  }

  /* ── Init on DOM Ready ─────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initScrollReveal();
    initHeroScroll();
    initShowcaseCards();
    initCompareTable();
    initFAQ();
    initSEOScrubbing();
  }

  // Re-initialize when a section is added or modified in the Theme Editor
  document.addEventListener('shopify:section:load', function() {
    init();
  });

})();
